import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X, Sparkles, ArrowRight, Check } from 'lucide-react';

// ────────────────────────────────────────────────────────────────────────────
// 402 Payment Required handler.
//
// The backend's PlanFeatureGuard responds with:
//   { feature, featureLabel, currentPlan, suggestedTier, message }
//
// The axios interceptor in lib/api.ts catches 402 responses, dispatches a
// CustomEvent that this component listens for, and surfaces a contextual
// upgrade prompt — telling the user exactly which feature they hit and what
// tier unlocks it. Way better than the previous "Internal server error" for
// a request that's actually a sales opportunity.
// ────────────────────────────────────────────────────────────────────────────

export interface UpgradePayload {
  feature?: string;
  featureLabel?: string;
  currentPlan?: string;
  suggestedTier?: string;
  message?: string;
  subscriptionInactive?: boolean;
}

export const UPGRADE_EVENT = 'brixlead:upgrade-required';

/** Fire from anywhere — typically the axios interceptor. */
export function triggerUpgradePrompt(detail: UpgradePayload) {
  window.dispatchEvent(new CustomEvent(UPGRADE_EVENT, { detail }));
}

const TIER_BLURB: Record<string, string> = {
  Starter:    'For small teams growing their pipeline',
  Growth:     'For teams scaling lead operations',
  Enterprise: 'Unlimited scale with dedicated support',
};

const TIER_HIGHLIGHTS: Record<string, string[]> = {
  Starter: [
    'Auto-merge duplicates',
    'Public REST API + webhooks',
    'Email templates & campaigns',
    'Advanced reports',
  ],
  Growth: [
    'Everything in Starter',
    'AI Assistant + drafts + smart alerts',
    'WhatsApp messaging & automation',
    'Audit log',
  ],
  Enterprise: [
    'Everything in Growth',
    'Google Calendar + Zoom sync',
    'Signed Data Processing Agreement',
    '99.5% uptime SLA',
  ],
};

export function UpgradeRequiredModal() {
  const [payload, setPayload] = useState<UpgradePayload | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<UpgradePayload>).detail;
      setPayload(detail ?? {});
    };
    window.addEventListener(UPGRADE_EVENT, handler);
    return () => window.removeEventListener(UPGRADE_EVENT, handler);
  }, []);

  // Close on Escape for accessibility.
  useEffect(() => {
    if (!payload) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setPayload(null);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [payload]);

  if (!payload) return null;

  const isRenew = !!payload.subscriptionInactive;
  const tier = payload.suggestedTier ?? 'Starter';
  const blurb = TIER_BLURB[tier] ?? '';
  const highlights = isRenew ? [] : (TIER_HIGHLIGHTS[tier] ?? []);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={() => setPayload(null)}
      />
      <div className="relative z-10 w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header gradient */}
        <div className="bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 px-6 py-5 text-white relative">
          <button
            onClick={() => setPayload(null)}
            className="absolute top-3 right-3 text-white/70 hover:text-white"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="h-5 w-5" />
            <span className="text-xs font-bold uppercase tracking-wider opacity-80">
              {isRenew ? 'Plan expired' : `${tier} plan feature`}
            </span>
          </div>
          <h2 className="text-2xl font-bold">
            {isRenew ? `Renew your ${payload.currentPlan ?? ''} plan` : (payload.featureLabel ?? 'This feature')}
          </h2>
          <p className="text-sm text-white/90 mt-1">
            {isRenew
              ? 'Your plan has expired. Renew to keep using paid features.'
              : <>Available on {tier} and above. {blurb && <>{blurb}.</>}</>}
          </p>
        </div>

        {/* Body */}
        <div className="p-6">
          <p className="text-sm text-slate-600 mb-4">
            {!isRenew && <>You're currently on the <strong>{payload.currentPlan ?? 'Free'}</strong> plan.</>}
            {payload.message && (
              <span className="block text-xs text-slate-400 mt-2">{payload.message}</span>
            )}
          </p>

          {highlights.length > 0 && (
            <>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                What you'll get on {tier}
              </p>
              <ul className="space-y-1.5 mb-6">
                {highlights.map((h) => (
                  <li key={h} className="flex items-start gap-2 text-sm text-slate-700">
                    <Check className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    {h}
                  </li>
                ))}
              </ul>
            </>
          )}

          <div className="flex gap-2">
            <button
              onClick={() => setPayload(null)}
              className="flex-1 px-4 py-2.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm font-medium text-slate-700"
            >
              Maybe later
            </button>
            <Link
              to="/billing"
              onClick={() => setPayload(null)}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] text-white text-sm font-semibold"
            >
              {isRenew ? 'Renew' : 'Upgrade'} <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
